
import { FC, useContext, useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import AuthContext from '@/contexts/auth/AuthContext';
import IHasChildren from '@/types/components/IHasChildren';
import { Permission } from '@/apis/dtos';
import Loading from '../layout/Loading';
import ErrorPage, { ErrorContent, ErrorTypeEnum } from '@/pages/ErrorPage';

interface IProtectedRouteProps extends IHasChildren { }

const ProtectedRoute: FC<IProtectedRouteProps> = ({
    children
}) => {
    const { data, loading, error } = useContext(AuthContext);

    const isValid = useMemo(() => {
        if (!data) return undefined;

        return !!(data.roles?.includes(Permission[Permission.RedactVerificationAccess]));
    }, [data]);

    // Unauthorized 
    //  - we don't have a user
    //  - we are not loading a user
    //  - there is an error in the attempt to get the user
    const isUnauthorized = useMemo(() => {
        return !data && !loading && error;
    }, [data, loading, error]);

    // Forbidden
    //  - we got a user
    //  - the user does not have the redaction verification access role
    const isForbidden = useMemo(() => {
        return data && !data.roles?.includes(Permission[Permission.RedactVerificationAccess]);
    }, [data])

    return (
        <>
            <Loading name="private-route" isLoading={isValid === undefined} isValidating={isValid === undefined} />
            {
                isUnauthorized ?
                    <ErrorContent errorType={ErrorTypeEnum.e401} /> :
                isForbidden ?
                    <ErrorContent errorType={ErrorTypeEnum.e403} /> :
                    children
            }
        </>
    )
}

export default ProtectedRoute